import React, { useRef, useState, useEffect } from 'react';
import Draggable from 'react-draggable';

const WindowFrame = ({ 
  isOpen, onClose, onMinimize, isMinimized, title, children, position, onPositionChange, 
  zIndex, onFocus, width = '600px', height = '400px',
  isTourActive = false, // Is guided tour currently running?
  isTourFocusWindow = false, // Is THIS the window currently focused in tour?
  tourSubtitle = '' // Contextual subtitle for tour mode
}) => {
  const nodeRef = useRef(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(position);

  // Sync position immediately when it changes - THIS IS CRITICAL
  useEffect(() => {
    setCurrentPosition(position);
  }, [position]);

  // Don't render if closed OR minimized
  if (!isOpen || isMinimized) return null;

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  // Handle Drag Stop: Save the new location
  const handleStop = (e, data) => {
    if (!isMaximized) {
      const newPos = { x: data.x, y: data.y };
      setCurrentPosition(newPos);
      onPositionChange(newPos);
    }
  };



  // Determine if window should be dimmed during tour
  const shouldDimWindow = isTourActive && !isTourFocusWindow;
  
  // Add glowing border if this is the focused window during tour
  const tourFocusBorder = isTourActive && isTourFocusWindow
    ? '3px solid rgba(255, 255, 255, 0.9)'
    : undefined;
  
  const tourFocusBoxShadow = isTourActive && isTourFocusWindow
    ? '0 0 40px rgba(255, 255, 255, 0.8), 0 0 80px rgba(0, 255, 157, 0.6), inset 0 0 20px rgba(255, 255, 255, 0.1)'
    : undefined;

  // Styles logic
  const windowStyle = isMaximized 
    ? { 
        width: 'calc(100% - 40px)', 
        height: 'calc(100vh - 162px)', 
        top: '62px', 
        left: '20px', 
        position: 'absolute',
        borderRadius: '12px',
        zIndex: (isTourActive && isTourFocusWindow) ? 10001 : (zIndex || 10), // Focused window above backdrop
        opacity: shouldDimWindow ? 0.3 : 1,
        filter: shouldDimWindow ? 'blur(4px)' : 'none',
        pointerEvents: shouldDimWindow ? 'none' : 'auto',
        transition: 'opacity 0.5s ease, filter 0.5s ease, border 0.3s ease, box-shadow 0.3s ease',
        border: tourFocusBorder,
        boxShadow: tourFocusBoxShadow
      }
    : { 
        width: width, 
        height: height,
        position: 'absolute',
        zIndex: (isTourActive && isTourFocusWindow) ? 10001 : (zIndex || 10), // Focused window above backdrop
        opacity: shouldDimWindow ? 0.3 : 1,
        filter: shouldDimWindow ? 'blur(4px)' : 'none',
        pointerEvents: shouldDimWindow ? 'none' : 'auto',
        transition: 'opacity 0.5s ease, filter 0.5s ease, border 0.3s ease, box-shadow 0.3s ease',
        border: tourFocusBorder,
        boxShadow: tourFocusBoxShadow
      };

  return (
    <Draggable 
      handle=".window-header" 
      position={isMaximized ? {x: 0, y: 0} : currentPosition}
      nodeRef={nodeRef}
      disabled={isMaximized || isTourActive} // Disable dragging during tour
      onStop={handleStop}
      bounds={{ top: 68, left: -50, right: window.innerWidth - 50, bottom: window.innerHeight - 150 }}
    >
      <div 
        ref={nodeRef} 
        className="window-frame"
        style={windowStyle}
        onMouseDownCapture={() => {
          if (onFocus) {
            onFocus();
          }
        }}
      >
        <div className="window-header" onDoubleClick={toggleMaximize} style={{ position: 'relative' }}>
          <div style={{ 
            position: 'absolute', 
            left: '50%', 
            transform: 'translateX(-50%)', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            whiteSpace: 'nowrap',
            pointerEvents: 'none' // Let clicks pass through to header for dragging
          }}>
            <span className="window-title">{title}</span>
            {isTourActive && isTourFocusWindow && tourSubtitle && (
              <>
                <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>:</span>
                <span className="window-subtitle" style={{
                  fontSize: '13px',
                  color: 'rgba(0, 255, 157, 0.6)', // Reduced opacity for hierarchy
                  fontWeight: '400',
                  letterSpacing: '0.3px',
                  fontFamily: 'monospace'
                }}>{tourSubtitle}</span>
              </>
            )}
          </div>
          
          {/* Controls remain on the right (auto-pushed by flex/absolute) */}
          <div style={{ flex: 1 }}></div> {/* Spacer */}
          <div className="window-controls">
            <div
              className="control-btn min-btn"
              onClick={onMinimize}
              style={{cursor: 'pointer'}}
            ></div>
            <div
              className="control-btn max-btn"
              onClick={toggleMaximize}
              style={{cursor: 'pointer'}}
            ></div>
            <div className="control-btn close-btn" onClick={onClose}></div>
          </div>
        </div>

        <div className="window-content">
          {children}
        </div>
      </div>
    </Draggable>
  );
};

export default WindowFrame;
